import axios from 'axios';
import { ErrorController } from '../../../../shared/errors/ErrorController.ts';
import { MathUtils } from '../../../../shared/utils/MathUtils.ts';
import { UIErrorHandler } from '../errors/UIErrorHandler.ts';
import { UIUtilsEjsVariables } from '../UIUtilsEjsVariables.ts';
import { StorageWrapper } from '../StorageWrapper.ts';
import { ErrorDefault } from '../../../../shared/errors/Errors.ts';
import { HTTPStatus } from '../../../../shared/errors/ErrorEnums.ts';
import { delay } from '@colonist/utils';
import { UITurnstile } from './UITurnstile.ts';
import { UIReconnectingBackgroundView } from '../ui_reconnection/UIReconnectingBackgroundView.ts';
export class UIAjaxResponse {
    get isSuccess() {
        // HTTP success between 200 and 299
        return MathUtils.isNumberBetween(this.status, 200, 299) && this.json.error == undefined;
    }
    constructor(response){
        this.status = response.status;
        this.headers = response.headers;
        this.json = response.data;
    }
}
export class UIAjaxWrapper {
    static async getRequestHandler(url, urlVariable, params, displayError = true, headers) {
        let urlToSend = url;
        if (urlVariable) urlToSend += '/' + urlVariable;
        if (UIUtilsEjsVariables.isUserOnDiscord) urlToSend = '/.proxy' + urlToSend;
        const config = {
            ...this.defaultConfig,
            headers: headers,
            params: params
        };
        const response = new UIAjaxResponse(await axios.get(urlToSend, config));
        this.storeAuthToken(response);
        if (UITurnstile.isChallengeResponse(response.headers)) {
            await UITurnstile.obtainPreClearanceCookie();
            return this.getRequestHandler(url, urlVariable, params, displayError, headers);
        }
        if (UITurnstile.isTokenResponse(response.headers)) {
            UIReconnectingBackgroundView.show({
                key: 'strings:popups.replay.pleaseWait'
            });
            let token;
            try {
                token = await UITurnstile.getToken();
            } catch (e) {
                UIReconnectingBackgroundView.remove();
                return undefined;
            }
            const newHeaders = {
                ...headers,
                turnstileToken: token
            };
            const result = await this.getRequestHandler(url, urlVariable, params, displayError, newHeaders);
            UIReconnectingBackgroundView.remove();
            return result;
        }
        if (response.isSuccess) return response;
        if (!displayError) return undefined;
        this.handleResponseError(response);
        return undefined;
    }
    static async postRequestWhenReady(url, queryParams, data, type = 'application/json') {
        while(!UIAjaxWrapper.csrfToken && !UIUtilsEjsVariables.isUserOnDiscordMobile){
            await delay(50);
        }
        return this.postRequestHandler(url, queryParams, data, type);
    }
    static async postRequestHandler(url, queryParams, data, type = 'application/x-www-form-urlencoded', additionalHeaders) {
        if (!UIAjaxWrapper.csrfToken && !UIUtilsEjsVariables.isUserOnDiscordMobile) {
            const clientNotReady = new ErrorDefault({
                key: 'strings:popups.errors.general.title'
            }, {
                key: 'strings:popups.errors.clientNotReady'
            }, HTTPStatus.ServiceUnavailable);
            UIErrorHandler.showDefaultErrorPopup(clientNotReady);
            return undefined;
        }
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const headers = {
            'Content-Type': type,
            'x-csrf-token': UIAjaxWrapper.csrfToken,
            ...additionalHeaders
        };
        if (UIUtilsEjsVariables.isUserOnDiscord) url = '/.proxy' + url;
        const response = new UIAjaxResponse(await axios.post(url, data, {
            headers: headers,
            ...this.defaultConfig,
            params: queryParams
        }));
        this.storeAuthToken(response);
        if (UITurnstile.isChallengeResponse(response.headers)) {
            await UITurnstile.obtainPreClearanceCookie();
            return this.postRequestHandler(url, queryParams, data, type);
        }
        if (UITurnstile.isTokenResponse(response.headers)) {
            let token;
            try {
                token = await UITurnstile.getToken();
            } catch (e) {
                return undefined;
            }
            return this.postRequestHandler(url, queryParams, data, type, {
                turnstileToken: token
            });
        }
        if (response.isSuccess) return response;
        this.handleResponseError(response);
        return undefined;
    }
    static storeAuthToken(response) {
        if (!UIUtilsEjsVariables.isUserOnDiscordMobile) return;
        if (response.headers['x-auth-token'] == undefined) return;
        StorageWrapper.setAuthToken(response.headers['x-auth-token']);
    }
    static handleResponseError(response) {
        const jsonError = response.json.error;
        if (jsonError == undefined) return;
        if (jsonError.type == undefined) return;
        const error = ErrorController.deserializeErrorData(jsonError);
        if (error == undefined) return;
        UIErrorHandler.handleError(error);
    }
    static get defaultConfig() {
        const resolveAnyResponseStatusCode = (status)=>true;
        return {
            // Mar 3, 2022 Omar: By default, axios will only resolve responses with status code 2XX, others will throw an error.
            // This is a workaround to make axios resolve all responses, and let the caller decide what to do with it.
            validateStatus: resolveAnyResponseStatusCode
        };
    }
}
UIAjaxWrapper.csrfToken = '';
axios.interceptors.request.use((config)=>{
    if (UIUtilsEjsVariables.isUserOnDiscordMobile) {
        const authToken = StorageWrapper.getAuthToken();
        if (authToken != null) config.headers.set('x-auth-token', authToken);
    }
    return config;
});
